import {
  DEFAULT_LOCALE,
  DEFAULT_NAMESPACE,
  NAMESPACES,
  SUPPORTED_LOCALES,
  loadLocaleBundle,
  type Locale,
  type LocaleBundle,
} from '@alola/i18n';
import i18next, { type i18n } from 'i18next';

/**
 * i18next instance (I18N-001), created with the languages already loaded. There is **no fallback
 * language**: a key missing in Arabic must never silently render English and produce a
 * mixed-language screen. Missing keys are reported through `onMissingKey` (console in development, a
 * failing assertion in tests) — and the CI key check prevents them from reaching a build (I18N-002).
 *
 * The application loads each language on demand (`ensureLocale`), so the entry chunk carries no
 * translations; this module therefore never imports the full resource tree.
 */
export function createI18nWith(
  onMissingKey: (key: string) => void,
  bundles: Partial<Record<Locale, LocaleBundle>>,
): i18n {
  const instance = i18next.createInstance();
  void instance.init({
    resources: bundles,
    lng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    fallbackLng: false,
    ns: [...NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,
    initAsync: false,
    returnNull: false,
    returnEmptyString: false,
    interpolation: { escapeValue: false },
    saveMissing: true,
    missingKeyHandler: (_languages, namespace, key) => {
      onMissingKey(`${namespace}:${key}`);
    },
  });
  return instance;
}

export function hasLocale(instance: i18n, locale: Locale): boolean {
  return instance.hasResourceBundle(locale, DEFAULT_NAMESPACE);
}

/** Loads a language's translations into the instance once; later calls return at once. */
export async function ensureLocale(instance: i18n, locale: Locale): Promise<void> {
  if (hasLocale(instance, locale)) return;
  const bundle = await loadLocaleBundle(locale);
  for (const namespace of NAMESPACES) {
    instance.addResourceBundle(locale, namespace, bundle[namespace], true, true);
  }
}
